import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import {assert} from 'Asserts';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import navigateToURL from 'lib/navigateToURL';
import notifications from 'notifications';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import {BaseChipList} from 'ui-library/chips/ChipList';
import {GRAY_DARK, GRAY_LIGHT, RED, RED_DARK} from 'ui-library/Colors';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import PlainSearchInput, {ClearType} from 'ui-library/PlainSearchInput';
import {
  RowSelectionMode,
  ScrollableTableColumn,
  ScrollableTableRow,
  TABLE_KEYS,
} from 'ui-library/scrollable/ScrollableTableConstants';
import SortableScrollableTable from 'ui-library/scrollable/SortableScrollableTable';
import copyToClipboard from 'util/clipboard/copyToClipboard';
import {InputChangeEvent} from 'util/Types';

import {MonomerQuery} from '../__generated/MonomerQuery';
import {MONOMER_QUERY} from '../MonomerConstants';
import {APIMonomer, createMonomers} from '../MonomersDAO';
import {CoreLibMonomer, HELMCoreMonomerLibraryAPI} from '../MonomersUtil';
import SmilesChemicalStructure from '../SmilesChemicalStructure';
import MonomerConfigurationPageContainer, {
  MONOMER_CONFIGURATION_PURPOSE,
} from './MonomerConfigurationPageContainer';

type MonomerQueryData = MonomerQuery['monomers'][number];

const TABLE_ROW_HEIGHT = 150;

interface HelmLibraryMonomerRowSortableData {
  name: string;
  symbol: string;
  naturalAnalogue: string;
  monomerType: string;
  smiles: string;
}

type HelmLibraryMonomerRowKeys = keyof HelmLibraryMonomerRowSortableData;

interface HelmLibraryMonomerRowData extends HelmLibraryMonomerRowSortableData {
  chemicalStructureImage: JSX.Element;
}

export interface HelmLibraryMonomerRow extends ScrollableTableRow {
  data: HelmLibraryMonomerRowData;
}

interface HelmLibraryMonomerColumn extends ScrollableTableColumn {
  sortKey?: HelmLibraryMonomerRowKeys;
}

const HELM_LIBRARY_TABLE_COLUMNS: Array<HelmLibraryMonomerColumn> = [
  {
    header: 'Name',
    accessorKey: 'name',
    flexGrow: 1,
    isCollapsible: false,
    isSortable: true,
    sortKey: 'name',
  },
  {
    header: 'Symbol',
    accessorKey: 'symbol',
    isCollapsible: true,
    isSortable: true,
    sortKey: 'symbol',
  },
  {
    header: 'Chemical structure',
    accessorKey: 'chemicalStructureImage',
    flexGrow: 1,
    isCollapsible: true,
    isSortable: false,
  },
  {
    header: 'Natural analogue',
    accessorKey: 'naturalAnalogue',
    isCollapsible: true,
    isSortable: true,
    sortKey: 'naturalAnalogue',
  },
  {
    header: 'Monomer type',
    accessorKey: 'monomerType',
    isCollapsible: true,
    isSortable: true,
    sortKey: 'monomerType',
  },
  {
    header: 'SMILES',
    accessorKey: 'smiles',
    flexGrow: 1,
    isCollapsible: true,
    isSortable: true,
    sortKey: 'smiles',
    wrapText: true,
  },
];

export interface MonomerLibraryHELMPageProps {
  viewOnly?: boolean;
}

export default function MonomerLibraryHELMPage({viewOnly = false}: MonomerLibraryHELMPageProps): JSX.Element {
  const [openSourceMonomers, setOpenSourceMonomers] = useState<Array<CoreLibMonomer>>([]);
  const [selectedMonomers, setSelectedMonomers] = useState<Array<string>>([]);
  const [query, setQuery] = useState<string>('');

  useEffect(() => {
    const fetchOpenSourceMonomers = async (): Promise<void> => {
      const mons = await HELMCoreMonomerLibraryAPI.fetch();
      setOpenSourceMonomers(mons.filter((mon) => mon.polymerType === 'RNA'));
    };
    fetchOpenSourceMonomers();
  }, []);

  const getMonInfo = (monomer: CoreLibMonomer | HelmLibraryMonomerRowData | MonomerQueryData): string =>
    `${monomer.monomerType.toLowerCase()}-${monomer.symbol}`;

  const selectedMonomerChips = selectedMonomers.map((info) => {
    const name = openSourceMonomers.find((mon) => getMonInfo(mon) === info)?.name;

    return (
      <Chip
        className={css(styles.monomerChip)}
        key={info}
        size={ChipSize.SMALL}
        text={name}
        onRemove={() => {
          setSelectedMonomers(selectedMonomers.filter((selectedInfo) => selectedInfo !== info));
        }}
      />
    );
  });

  return (
    <div
      className={css(styles.pageContainer)}
      {...getTestProps({component: 'MonomerLibraryHELMPage', element: 'root'})}
    >
      <MonomerConfigurationPageContainer
        methodTitle="Search HELM Library"
        purpose={viewOnly ? MONOMER_CONFIGURATION_PURPOSE.VIEW : MONOMER_CONFIGURATION_PURPOSE.CREATE}
      />
      <div className={css(styles.selectedMonomersSection)}>
        <span className={css(styles.boldHeader)}>Selected monomers</span>
        <div className={css(styles.selectedMonomerChips)}>
          {selectedMonomers.length > 0 ? (
            <BaseChipList
              chips={selectedMonomerChips}
              chipSize={ChipSize.SMALL}
              maxChips={8} // rough estimate for 1-2 rows
              isVertical={false}
            />
          ) : (
            <span className={css(styles.noneSelectedText)}>None selected</span>
          )}
        </div>
      </div>
      <BenchlingQuery<MonomerQuery>
        query={MONOMER_QUERY}
        renderLoading={() => <CenteredHorizontalThrobber />}
        renderCachedDataWhenRefetching={true}
      >
        {({monomers: existingMonomersData}, {refetch}) => {
          const existingMonomers = new Set(existingMonomersData.map((mon) => getMonInfo(mon)));

          const handleRowsSelect = (rows: Array<HelmLibraryMonomerRow>, isSelected: boolean): void => {
            const modified = rows.map((row) => getMonInfo(row.data));
            let newSelected;
            if (isSelected) {
              const toAdd = modified.filter(
                (info) => !selectedMonomers.includes(info) && !existingMonomers.has(info)
              );
              newSelected = selectedMonomers.concat(toAdd);
            } else {
              newSelected = selectedMonomers.filter((info) => !modified.includes(info));
            }
            setSelectedMonomers(newSelected);
          };

          const handleRowClick = (row: HelmLibraryMonomerRow): void => {
            if (existingMonomers.has(getMonInfo(row.data))) {
              return;
            }
            if (selectedMonomers.includes(getMonInfo(row.data))) {
              setSelectedMonomers(selectedMonomers.filter((info) => info !== getMonInfo(row.data)));
            } else {
              setSelectedMonomers(selectedMonomers.concat(getMonInfo(row.data)));
            }
          };

          const rows: Array<HelmLibraryMonomerRow> = openSourceMonomers
            .filter(
              (monomer) =>
                query === '' ||
                monomer.name.toLowerCase().includes(query.toLowerCase()) ||
                monomer.symbol.toLowerCase().includes(query.toLowerCase())
            )
            .map((monomer) => {
              const monomerExistsAlready = existingMonomers.has(getMonInfo(monomer));
              return {
                id: getMonInfo(monomer),
                isSelected: selectedMonomers.includes(getMonInfo(monomer)),
                displayAsSelected: monomerExistsAlready,
                isSelectorDisabled: monomerExistsAlready,
                tooltipText: monomerExistsAlready ? 'Already added to the monomer library' : undefined,
                data: {
                  name: monomer.name,
                  symbol: monomer.symbol,
                  naturalAnalogue: monomer.naturalAnalog,
                  monomerType: monomer.monomerType,
                  smiles: monomer.smiles,
                  chemicalStructureImage: (
                    <SmilesChemicalStructure
                      smiles={monomer.smiles}
                      className={css(styles.chemicalStructureContainer)}
                      svgClassName={css(styles.chemicalStructureSvg)}
                    />
                  ),
                },
              };
            });

          const monomerTable = (
            <SortableScrollableTable
              className={css(styles.table)}
              canResizeColumns={true}
              columns={HELM_LIBRARY_TABLE_COLUMNS}
              getRowContentHeight={() => TABLE_ROW_HEIGHT}
              emptyTableText="No monomers were found."
              getSortValue={(row: HelmLibraryMonomerRow, sortKey: HelmLibraryMonomerRowKeys) =>
                row.data[sortKey]
              }
              initialRows={rows}
              initialSort={[{sortKey: 'name'}]}
              tableKey={TABLE_KEYS.MONOMER_LISTING_TABLE}
              calcLeftPadding={() => 40}
              matchParentHeight={true}
              rowSelectionMode={RowSelectionMode.MULTIPLE}
              onRowsSelect={handleRowsSelect}
              onRowClick={handleRowClick}
              isCompact={true}
            />
          );

          const addNewMonomers = async (): Promise<void> => {
            const newMonomers: Array<APIMonomer> = selectedMonomers.map((selectedMonomerInfo) => {
              const coreLibMonomer = openSourceMonomers.find(
                (openSourceMonomer) => getMonInfo(openSourceMonomer) === selectedMonomerInfo
              );
              assert(
                coreLibMonomer != null,
                `Could not find HELM library monomer for ${selectedMonomerInfo}`
              );
              const {name, symbol, naturalAnalog, smiles} = coreLibMonomer;
              return {name, symbol, naturalAnalogue: naturalAnalog, smiles};
            });
            try {
              LoadingScreen.show('Creating...');
              const result = await createMonomers(newMonomers);
              await refetch();
              notifications.success(`Imported (${result.length}) monomers into library`);
              navigateToURL('/monomers');
              // eslint-disable-next-line benchlint/always-throw-from-catch
            } catch (e) {
              notifications.notifyError(e);
            } finally {
              LoadingScreen.hide();
            }
          };

          const copyMonomerSymbolsToClipboard = (): void => {
            const symbols: Array<string> = selectedMonomers.map((selectedMonomerInfo) => {
              const coreLibMonomer = openSourceMonomers.find(
                (openSourceMonomer) => getMonInfo(openSourceMonomer) === selectedMonomerInfo
              );
              assert(
                coreLibMonomer != null,
                `Could not find HELM library monomer for ${selectedMonomerInfo}`
              );
              return coreLibMonomer.symbol;
            });
            copyToClipboard(symbols.join(', '), {
              showToastOnSuccess: true,
              toastText: `Copied (${symbols.length}) monomer symbols to clipboard`,
            });
          };

          return (
            <>
              <div className={css(styles.tableSection)}>
                <div className={css(styles.tableHeaderSection)}>
                  <span className={css(styles.boldHeader)}>HELM Library</span>
                  <PlainSearchInput
                    className={css(styles.searchBar)}
                    clearType={ClearType.X}
                    onChange={(e: InputChangeEvent) => {
                      setQuery(e.target.value);
                    }}
                    onEscape={() => {
                      setQuery('');
                    }}
                    placeholder="Search by symbol or name"
                    value={query}
                  />
                </div>
                {openSourceMonomers.length > 0 ? monomerTable : <CenteredHorizontalThrobber />}
              </div>
              <div className={css(styles.submitBar)}>
                <div className={css(styles.buttonsContainer)}>
                  <Button
                    buttonStyle={ButtonStyle.TERTIARY_GRAY}
                    onClick={() => {
                      navigateToURL('/monomers');
                    }}
                    size={ButtonSize.MEDIUM}
                    text="Cancel"
                  />
                  <Button
                    buttonStyle={ButtonStyle.SECONDARY}
                    text={
                      viewOnly
                        ? `Copy ${selectedMonomers.length} symbols`
                        : `Add (${selectedMonomers.length}) monomers`
                    }
                    size={ButtonSize.MEDIUM}
                    disabled={selectedMonomers.length === 0}
                    onClick={viewOnly ? copyMonomerSymbolsToClipboard : addNewMonomers}
                    dataTestAttrs={{
                      component: 'MonomerLibraryHELMPage',
                      element: 'addMonomersButton',
                    }}
                  />
                </div>
              </div>
            </>
          );
        }}
      </BenchlingQuery>
    </div>
  );
}

const styles = StyleSheet.create({
  chemicalStructureContainer: {
    height: `${TABLE_ROW_HEIGHT}px`,
    textAlign: 'center',
  },
  chemicalStructureSvg: {
    width: '100%',
    height: '100%',
  },
  pageContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  table: {
    height: '100%',
  },
  tableSection: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    borderTop: `1px solid ${GRAY_LIGHT}`,
  },
  selectedMonomersSection: {
    flex: '0 0 auto',
    marginLeft: 40,
    maxHeight: 120,
    minHeight: 65,
  },
  selectedMonomerChips: {
    overflowY: 'auto',
    maxHeight: 85,
    marginTop: 8,
  },
  monomerChip: {
    marginBottom: 8,
  },
  boldHeader: {
    ...FONT_PROXIMA_13,
    fontWeight: 'bold',
  },
  tableHeaderSection: {
    margin: '17px 0px 23px 40px',
  },
  submitBar: {
    flex: '0 0 auto',
    boxSizing: 'border-box',
    padding: 16,
    gap: 10,
  },
  cancelButton: {
    color: RED,
    ':hover': {
      cursor: 'pointer',
      color: RED_DARK,
    },
  },
  buttonsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  searchBar: {
    width: 350,
    marginTop: 4,
  },
  noneSelectedText: {
    ...FONT_PROXIMA_13,
    color: GRAY_DARK,
    marginTop: 8,
  },
});
